import { filesApi } from "@/api/files-api";
import { workspaceApi } from "@/api/workspace-api";
import { useWorkspaceSettings } from "@/hooks/use-workspace-settings";
import { validateSlug } from "@/lib/slug-utils";
import { WorkspaceTypeOptions, normalizeWorkspaceType } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, Eye, Loader2, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import posthog from "posthog-js";

const AVATAR_FILE_TYPE = "company avatar";

type SlugStatus =
  | "idle"
  | "checking"
  | "available"
  | "taken"
  | "invalid"
  | "saving"
  | "saved";

export default function GeneralSettings() {
  const {
    workspace,
    isLoading,
    activeWorkspaceId,
    updateWorkspaceMutation,
    invalidateWorkspace,
  } = useWorkspaceSettings();

  // Workspace
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [type, setType] = useState("");
  const [saved, setSaved] = useState(false);

  // Portal slug
  const [slug, setSlug] = useState("");
  const [slugStatus, setSlugStatus] = useState<SlugStatus>("idle");
  const [slugError, setSlugError] = useState("");
  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedSlug = useRef("");

  // Logo
  const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(
    null,
  );
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!workspace) return;
    setName(workspace.opportunity_name || "");
    setWebsite(workspace.website_url || "");
    setType(normalizeWorkspaceType(workspace.opportunity_type));
    setSlug(workspace.portal_slug || "");
    lastSavedSlug.current = workspace.portal_slug || "";
  }, [workspace]);

  useEffect(
    () => () => {
      if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    },
    [],
  );

  const companyAvatarFiles =
    workspace?.affiliated_company?.files?.filter(
      (file: { file_type: string }) => file.file_type === AVATAR_FILE_TYPE,
    ) || [];
  const currentAvatar =
    companyAvatarFiles.length > 0 ? companyAvatarFiles[0] : null;

  // ── Slug handlers ─────────────────────────────────────────────────────
  const handleSlugChange = (value: string) => {
    const next = value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setSlug(next);
    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    if (!next) {
      setSlugStatus("idle");
      setSlugError("");
      return;
    }
    const validation = validateSlug(next);
    if (!validation.valid) {
      setSlugStatus("invalid");
      setSlugError(validation.error || "Invalid slug format");
      return;
    }
    if (next === lastSavedSlug.current) {
      setSlugStatus("saved");
      setSlugError("");
      return;
    }
    setSlugStatus("checking");
    setSlugError("");
    slugCheckTimer.current = setTimeout(async () => {
      try {
        const existingSlugs = await workspaceApi.fetchExistingSlugs();
        const isTaken = existingSlugs.some(
          (s) => s === next && next !== lastSavedSlug.current,
        );
        setSlugStatus(isTaken ? "taken" : "available");
        setSlugError(isTaken ? "This URL is already taken" : "");
      } catch {
        setSlugStatus("available");
        setSlugError("");
      }
    }, 500);
  };

  const renderSlugStatus = () => {
    if (slugStatus === "idle") return null;
    const colorClass =
      slugStatus === "checking" || slugStatus === "saving"
        ? "text-muted-foreground"
        : slugStatus === "available" || slugStatus === "saved"
          ? "text-green-600"
          : "text-red-500";
    return (
      <div className={`mt-1 flex items-center gap-1.5 text-xs ${colorClass}`}>
        {slugStatus === "checking" && (
          <>
            <Loader2 className="h-3 w-3 animate-spin" /> Checking availability...
          </>
        )}
        {slugStatus === "available" && <>✓ Available</>}
        {slugStatus === "saved" && <>✓ Saved</>}
        {(slugStatus === "taken" || slugStatus === "invalid") && (
          <>✗ {slugError}</>
        )}
      </div>
    );
  };

  // ── Logo handlers ─────────────────────────────────────────────────────
  const handleAvatarFileSelection = (files: FileList | null) => {
    if (!files?.length) return;
    setSelectedAvatarFile(files[0]);
  };

  const handleAvatarUpload = async () => {
    if (!selectedAvatarFile || !workspace?._id || !activeWorkspaceId) return;
    setIsUploadingAvatar(true);
    setUploadProgress(0);
    try {
      const formData = {
        file: selectedAvatarFile,
        file_name: "company_avatar.jpg",
        file_type: AVATAR_FILE_TYPE,
        object_ref: "company",
        object_ref_value: workspace.affiliated_company?._id || "",
      };
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 5;
        });
      }, 100);
      await filesApi.uploadFile(
        formData,
        workspace.affiliated_company?._id,
        activeWorkspaceId,
      );
      clearInterval(progressInterval);
      setUploadProgress(100);
      toast.success("Company logo uploaded");
      setSelectedAvatarFile(null);
      setAvatarDialogOpen(false);
      invalidateWorkspace();
    } catch (error) {
      console.error("Avatar upload error:", error);
      toast.error("Failed to upload company logo");
    } finally {
      setIsUploadingAvatar(false);
      setUploadProgress(0);
    }
  };

  const handleAvatarDelete = async () => {
    if (!workspace?.affiliated_company?._id || !activeWorkspaceId) return;
    try {
      await filesApi.deleteFile(
        AVATAR_FILE_TYPE,
        workspace.affiliated_company._id,
        activeWorkspaceId,
      );
      toast.success("Company logo removed");
      invalidateWorkspace();
    } catch (error) {
      console.error("Avatar delete error:", error);
      toast.error("Failed to remove company logo");
    }
  };

  const isDirty =
    name !== (workspace?.opportunity_name || "") ||
    website !== (workspace?.website_url || "") ||
    type !== (workspace?.opportunity_type || "") ||
    (slug !== "" && slug !== lastSavedSlug.current);

  // ── Save ──────────────────────────────────────────────────────────────
  const handleSave = () => {
    if (slugStatus === "invalid" || slugStatus === "taken") {
      toast.error("Fix the portal URL before saving.");
      return;
    }
    const payload: {
      opportunity_name: string;
      website_url: string;
      opportunity_type: string;
      portal_slug?: string;
    } = {
      opportunity_name: name,
      website_url: website,
      opportunity_type: type,
    };
    if (slug && slug !== lastSavedSlug.current) {
      payload.portal_slug = slug;
    }
    updateWorkspaceMutation.mutate(payload, {
      onSuccess: () => {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        if (payload.portal_slug) {
          lastSavedSlug.current = payload.portal_slug;
          posthog.capture("settings_workspace_portal_link_customized", {
            workspace_id: activeWorkspaceId,
            new_link_slug: payload.portal_slug,
          });
        }
        posthog.capture("settings_workspace_general_updated", {
          workspace_id: activeWorkspaceId,
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
    <div className="mx-auto max-w-2xl px-6 py-8 pb-24">
      <h1 className="text-lg font-semibold text-foreground">General</h1>
      <p className="mt-0.5 text-sm text-muted-foreground">
        Workspace identity. Read by the Applications portal and Raiseboard.
      </p>

      {/* Workspace info */}
      <section className="mt-8">
        <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
          Workspace
        </h2>
        <div className="space-y-4 rounded-lg border border-border bg-card p-4">
          <div className="space-y-2">
            <Label htmlFor="workspace-name">Name</Label>
            <Input
              id="workspace-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="workspace-website">Website</Label>
            <Input
              id="workspace-website"
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="workspace-type">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="workspace-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {WorkspaceTypeOptions.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Portal URL */}
      <section className="mt-8">
        <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
          Portal URL
        </h2>
        <div className="space-y-3 rounded-lg border border-border bg-card p-4">
          <div className="space-y-2">
            <Label htmlFor="portal-slug">Slug</Label>
            <Input
              id="portal-slug"
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="your-fund-slug"
              className="max-w-[280px] font-mono"
            />
            {renderSlugStatus()}
            <p className="pt-1 text-xs text-muted-foreground">
              Used by the Applications portal and Raiseboard:
            </p>
            <div className="space-y-1 font-mono text-xs">
              <div>
                <span className="text-muted-foreground">Applications:</span>{" "}
                <span className="text-foreground">
                  {window.location.origin}/p/{slug || "<slug>"}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Raiseboard:</span>{" "}
                <span className="text-foreground">
                  {window.location.origin}/rb/{slug || "<slug>"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logo */}
      <section className="mt-8">
        <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
          Logo
        </h2>
        <div className="rounded-lg border border-border bg-card p-4">
          {currentAvatar && (
            <div className="group relative mb-3 flex items-center rounded-lg border bg-card p-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 overflow-hidden rounded-md border border-border bg-gray-100">
                  <img
                    src={currentAvatar.file_url}
                    alt="Company Logo"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="space-y-0.5">
                  <div className="text-sm font-medium leading-none">
                    Company Logo
                  </div>
                  <div className="text-xs text-muted-foreground">
                    IMAGE · {currentAvatar.file_size || "—"}
                  </div>
                </div>
              </div>
              <div className="ml-auto flex gap-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <div className="flex h-[400px] w-full items-center justify-center">
                      <img
                        src={currentAvatar.file_url}
                        alt="Company Logo"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  </DialogContent>
                </Dialog>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={handleAvatarDelete}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <Dialog open={avatarDialogOpen} onOpenChange={setAvatarDialogOpen}>
            <DialogTrigger asChild>
              <Button type="button" variant="outline" size="sm">
                <Upload className="mr-2 h-4 w-4" />
                {currentAvatar ? "Replace logo" : "Upload logo"}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold">Upload company logo</h3>
                  <p className="text-sm text-muted-foreground">
                    Select an image file (square 1:1 format recommended).
                  </p>
                </div>
                <div
                  className="rounded-lg border-2 border-dashed border-primary p-8 text-center"
                  onDrop={(e) => {
                    e.preventDefault();
                    handleAvatarFileSelection(e.dataTransfer.files);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    ref={avatarFileInputRef}
                    type="file"
                    id="avatar-upload"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) =>
                      handleAvatarFileSelection(e.target.files)
                    }
                  />
                  <label
                    htmlFor="avatar-upload"
                    className="flex cursor-pointer flex-col items-center"
                  >
                    <Upload className="mb-2 h-8 w-8 text-primary" />
                    {selectedAvatarFile ? (
                      <span className="max-w-[200px] truncate text-sm font-medium text-primary">
                        {selectedAvatarFile.name}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        Drag and drop or click to upload
                      </span>
                    )}
                  </label>
                </div>
                {selectedAvatarFile && (
                  <div className="space-y-4">
                    {isUploadingAvatar && (
                      <div className="space-y-2">
                        <Progress value={uploadProgress} />
                        <p className="text-center text-sm text-muted-foreground">
                          Uploading... {uploadProgress}%
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        className="flex-1"
                        onClick={handleAvatarUpload}
                        disabled={isUploadingAvatar}
                      >
                        {isUploadingAvatar ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="mr-2 h-4 w-4" />
                        )}
                        {isUploadingAvatar ? "Uploading..." : "Upload logo"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setSelectedAvatarFile(null);
                          if (avatarFileInputRef.current)
                            avatarFileInputRef.current.value = "";
                        }}
                        disabled={isUploadingAvatar}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          <p className="mt-2 text-xs text-muted-foreground">
            PNG, JPG, or SVG. Max 2MB. Shown on your public portal and Raise
            Board.
          </p>
        </div>
      </section>

    </div>
    <div className="sticky bottom-0 border-t border-border bg-background/90 backdrop-blur">
      <div className="mx-auto flex max-w-2xl justify-end px-6 py-3">
        <Button
          onClick={handleSave}
          disabled={updateWorkspaceMutation.isPending || !isDirty}
          className={saved ? "bg-green-600 hover:bg-green-600" : undefined}
        >
          {saved && <Check className="mr-1 h-3.5 w-3.5" />}
          {updateWorkspaceMutation.isPending
            ? "Saving..."
            : saved
              ? "Saved"
              : "Save Changes"}
        </Button>
      </div>
    </div>
    </>
  );
}
